import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { usePlatformConfig } from "../lib/platformConfig";
import BrandSwitcher from "./BrandSwitcher";
import {
    Image as ImageIcon,
    Library,
    Settings,
    LogOut,
    Sparkles,
    Users as UsersIcon,
    BarChart3,
    ScrollText,
    Plus,
    GraduationCap,
    Type,
} from "lucide-react";

export default function Sidebar() {
    const { user, logout } = useAuth();
    const { config } = usePlatformConfig();
    const navigate = useNavigate();
    const isAdmin = user?.role === "admin" || user?.role === "super_admin";

    return (
        <aside className="w-[240px] shrink-0 h-screen sticky top-0 border-r hairline border-ink-600 bg-ink-900/40 flex flex-col">
            {/* brand / logo */}
            <div className="px-4 py-4 flex items-center gap-3 border-b hairline border-ink-600 shrink-0">
                <img
                    src={config.logo_url || "/krea-logo.png"}
                    alt={config.platform_name || "Krea.AI"}
                    className="h-8 w-auto object-contain shrink-0"
                    draggable={false}
                />
                <span className="font-mono text-[8px] tracking-[0.3em] text-fog-400 uppercase">
                    .AI
                </span>
            </div>

            {/* brand switcher */}
            <div className="px-3 pt-3 shrink-0">
                <BrandSwitcher />
            </div>

            {/*
             * Primary CTA available to EVERY role. Takes the user straight to
             * the compose surface — the chat record is created lazily on first
             * send so this never leaves empty rows in the history.
             */}
            <div className="px-3 pt-3 shrink-0">
                <button
                    data-testid="sidebar-new-chat-btn"
                    onClick={() => {
                        // Tell any listening Chat to hard-reset its state.
                        // This runs BEFORE navigate so the key bump and the
                        // URL change happen in the same render cycle — works
                        // whether the user is already on /chats or on any
                        // other route.
                        window.dispatchEvent(new CustomEvent("kc:new-chat-requested"));
                        navigate("/chats");
                    }}
                    className="group w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl bg-periwinkle-500 hover:bg-periwinkle-400 text-ink-on font-medium shadow-glow-sm hover:shadow-glow transition-all text-sm"
                    title="Ir al chat para crear uno nuevo"
                >
                    <Plus className="w-4 h-4 transition-transform group-hover:rotate-90 duration-300" />
                    <span className="font-display tracking-tight">Nuevo chat</span>
                </button>
            </div>

            {/* nav */}
            <nav className="px-3 py-4 flex flex-col gap-1 shrink-0">
                <NavItem to="/" icon={Sparkles} label="Inicio" end />
                <NavItem to="/library" icon={Library} label="Biblioteca" />
                <NavItem to="/references" icon={ImageIcon} label="Referencias" />
                {/*
                 * Copy Studio — text generation surface (GPT-5.2). Same
                 * "lazy chat" approach as the image studio: clicking the
                 * link lands the user on /copy without creating an
                 * empty chat row. NUEVO badge until the feature has
                 * been in production for a month.
                 */}
                <NavItem to="/copy" icon={Type} label="Copy Studio" badge="NUEVO" />
                <NavItem to="/tutoriales" icon={GraduationCap} label="Tutoriales" />
                {isAdmin && <NavItem to="/stats" icon={BarChart3} label="Estadísticas" />}
                {isAdmin && <NavItem to="/logs" icon={ScrollText} label="Logs" />}
                {isAdmin && <NavItem to="/users" icon={UsersIcon} label="Usuarios" />}
                {isAdmin && <NavItem to="/admin" icon={Settings} label="Admin" />}
            </nav>

            <div className="flex-1" />

            {/* user footer */}
            <div className="border-t hairline border-ink-600 px-4 py-4 shrink-0">
                <div className="flex items-center gap-3">
                    <div
                        className="w-9 h-9 rounded-full border hairline border-ink-600 flex items-center justify-center text-xs font-mono uppercase shrink-0"
                        style={{
                            background: user?.avatar_color || "#a5a6ff",
                            color: "#060818",
                        }}
                    >
                        {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                            <p className="text-sm text-fog-100 truncate font-medium">{user?.name}</p>
                            <RoleBadge role={user?.role} />
                        </div>
                        <p className="text-[10px] text-fog-400 font-mono truncate">
                            {user?.email}
                        </p>
                    </div>
                    <button
                        data-testid="sidebar-logout-btn"
                        onClick={logout}
                        className="text-fog-400 hover:text-periwinkle-800 transition-colors"
                        aria-label="Salir"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </aside>
    );
}

function NavItem({ to, icon: Icon, label, end = false, badge = null }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={`sidebar-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                    isActive
                        ? "bg-periwinkle-500 text-periwinkle-ink font-semibold shadow-glow-sm"
                        : "text-fog-300 hover:bg-ink-800/60 hover:text-fog-100"
                }`
            }
        >
            <Icon className="w-4 h-4" />
            <span className="flex-1">{label}</span>
            {badge && (
                <span className="font-mono text-[8px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-periwinkle-500/20 text-periwinkle-800 border hairline border-periwinkle-500/30">
                    {badge}
                </span>
            )}
        </NavLink>
    );
}

const ROLE_STYLES = {
    super_admin: { label: "Super", className: "border-periwinkle-500/50 text-periwinkle-800" },
    admin: { label: "Admin", className: "border-emerald-400/40 text-emerald-300" },
    user: { label: "User", className: "border-ink-600 text-fog-400" },
};

function RoleBadge({ role }) {
    const style = ROLE_STYLES[role] || ROLE_STYLES.user;
    return (
        <span
            data-testid={`sidebar-role-badge-${role || "user"}`}
            className={`font-mono text-[8px] tracking-widest uppercase px-1.5 py-0.5 rounded border hairline shrink-0 ${style.className}`}
        >
            {style.label}
        </span>
    );
}
